/**
 * The status marks on a shift card, each its own rounded shape so it reads without colour
 * (plan 0029, owner 2026-09-24): colour only reinforces the shape, and the icon stands alone,
 * with no box, border or fill behind it. Filled where the shape needs it (approved, on the job),
 * which is why these are not in the stroke-only set in ui/icons.
 */
export type StatusKind = 'missing' | 'onjob' | 'waiting' | 'approved' | 'offered' | 'swap';

const PATH: Record<StatusKind, JSX.Element> = {
  missing: (
    <g fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="8" cy="8" r="6.2" />
      <circle cx="8" cy="8" r="3.2" />
      <circle cx="8" cy="8" r="0.9" fill="currentColor" />
    </g>
  ),
  onjob: (
    <g>
      <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 1.8a6.2 6.2 0 0 1 0 12.4z" fill="currentColor" />
    </g>
  ),
  waiting: (
    <g fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.6V8l2.4 1.6" />
    </g>
  ),
  approved: (
    <g>
      <rect x="1.5" y="1.5" width="13" height="13" rx="3.2" fill="currentColor" />
      <path
        d="M4.8 8.2l2.2 2.2 4.4-4.8"
        fill="none"
        stroke="var(--card)"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  ),
  offered: (
    <g fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2.5h4v4M13.5 2.5L7.5 8.5" />
      <path d="M12 9.5v2.8a1.7 1.7 0 0 1-1.7 1.7H3.7A1.7 1.7 0 0 1 2 12.3V5.7A1.7 1.7 0 0 1 3.7 4h2.8" />
    </g>
  ),
  swap: (
    <g fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 5.5h10M10 3l2.5 2.5L10 8M13.5 10.5h-10M6 8l-2.5 2.5L6 13" />
    </g>
  ),
};

/** Decorative: the caller's Tip and aria-label carry the words. */
/** With a label the icon is the accessible name (a card's mark); without, it is decoration (the legend). */
export function StatusIcon({ kind, label }: { kind: StatusKind; label?: string }) {
  if (!label)
    return (
      <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false" data-kind={kind}>
        {PATH[kind]}
      </svg>
    );
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" role="img" focusable="false" data-kind={kind}>
      <title>{label}</title>
      {PATH[kind]}
    </svg>
  );
}
